import React, { useContext, useState } from 'react';
import { APICredentials } from '../../../types/types';
import ShowSnackbar from '../../../Services/snackbar';
import ModalWrapper from '../../../common/ModalWrapper';
import { AuthContext } from '../../../auth/authContext';
import ConfirmationModal from '../../../common/ConfirmationModal';
import { useApiCredentialsList, useDeleteCredential, useGenerateCredential } from '../hooks/useApiCredentials';
import { Link, Navigate } from 'react-router-dom';
import { EntityServices } from '../../../Services/EntityServices';
import { END_POINTS_PATH } from '../../../routes/paths';
import { HOST_API_URL_V2 } from '../../../config';
import { setSession } from '../../../auth/utils';

interface GeneratedCredential {
    apiKey: string;
    clientSecret: string;
    isActive: boolean;
    id: string;
    createdAt: string;
}

const DahabCredentialsList = () => {
    const { data: items = [], isLoading: loading, refetch } = useApiCredentialsList();
    const [search, setSearch] = useState('');
    const { id: userId } = useContext(AuthContext);
    const [showConfirm, setShowConfirm] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<{ id: string } | null>(null);
    const [showResultModal, setShowResultModal] = useState(false);
    const [generatedData, setGeneratedData] = useState<GeneratedCredential | null>(null);
    const generateMutation = useGenerateCredential(userId || '');
    const deleteMutation = useDeleteCredential();
    const { username, setAuthenticated } = useContext(AuthContext);

    const generating = generateMutation.status === 'pending';
    const deleting = deleteMutation.status === 'pending';

    const handleDeleteRequest = (id: string) => {
        setItemToDelete({ id });
        setShowDeleteConfirm(true);
    };

    const onDelete = () => {
        if (!itemToDelete) return;
        setShowDeleteConfirm(false);
        deleteMutation.mutate(itemToDelete.id, {
            onSuccess: () => refetch()
        });
    };

    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<APICredentials | null>(null);

    const openEdit = (id: string) => {
        const item = (items as APICredentials[]).find(i => i.id === id) || null;
        if (item) {
            setEditing(item);
            setModalOpen(true);
        }
    };

    const onModalSaved = () => {
        setModalOpen(false);
        refetch();
    };

    const LogOut = () => {
        setSession(null);
        setAuthenticated(false);
        window.location.href = '/login';
    };

    const toggleActive = async (id: string, active: boolean) => {
        try {
            const svc = new EntityServices<APICredentials>();
            const res = await svc.AddFromAnotherModelAsync<any>(`${HOST_API_URL_V2}${END_POINTS_PATH.APICredentials}/${userId}/${id}/activate?active=${active}`, {}, undefined, true);
            if (res && res.statusCode === 200) {
                LogOut();
            } else {
                ShowSnackbar(res?.message || 'Activation failed', 'danger');
            }
        } catch (err) {
            ShowSnackbar('Activation failed', 'danger');
        }
    };

    const handleGenerateConfirm = () => {
        setShowConfirm(true);
    };

    const handleGenerate = () => {
        setShowConfirm(false);
        generateMutation.mutate(undefined, {
            onSuccess: (data) => {
                setGeneratedData(data);
                setShowResultModal(true);
                refetch();
            }
        });
    };

    const filtered = (items as APICredentials[]).filter(it => {
        if (!search) return true; const s = search.toLowerCase(); return it.clientSecret.toLowerCase().includes(s);
    });
    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">API Key Management</h1>
                    <p className="text-muted">Manage your API secrets for secure access to Dahab Webservice services.</p>
                </div>
                <div className="ms-auto pageheader-btn">
                    <Link to="/settings" className="btn btn-dark me-2 hover:bg-darken-400">
                        <i className="fe fe-arrow-left"></i> Back to Settings
                    </Link>
                    <button className="btn btn-light me-2" onClick={() => refetch()} disabled={loading}>
                        <i className="fe fe-refresh-cw"></i> Refresh
                    </button>
                    <button className="btn btn-primary" onClick={handleGenerateConfirm} disabled={loading || generating}>
                        <i className="fe fe-plus"></i> Generate New
                    </button>
                </div>
            </div>

            <div className="card mb-3">
                <div className="card-body">
                    <div className="row g-2 mb-3 align-items-center">
                        <div className="col-md-4">
                            <div className="input-group">
                                <span className="input-group-text bg-transparent border-end-0">
                                    <i className="fe fe-search"></i>
                                </span>
                                <input className="form-control border-start-0" placeholder="Search by secret" value={search} onChange={(e) => setSearch(e.target.value)} />
                            </div>
                        </div>
                    </div>

                    {loading && <div className="text-center p-3">Loading...</div>}
                    {!loading && items.length === 0 && (
                        <div className="text-center p-5">
                            <i className="fe fe-key fs-1 text-muted mb-3"></i>
                            <h4 className="text-muted">No API secrets yet</h4>
                            <p className="text-muted">Generate your first API secret to get started.</p>
                            <button className="btn btn-primary" onClick={handleGenerateConfirm} disabled={loading || generating}>
                                <i className="fe fe-plus"></i> Generate API Secret
                            </button>
                        </div>
                    )}
                    {!loading && items.length > 0 && (
                        <div className="table-responsive">
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        <th>Client Secret</th>
                                        <th>Created At</th>
                                        <th>Is Active</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((it) => (
                                        <tr key={it.id}>
                                            <td>
                                                <span style={{ fontFamily: 'monospace' }}>{it.clientSecret}</span>
                                            </td>
                                            <td>
                                                {new Date(it.createdAt).toLocaleString('en-US', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: '2-digit'
                                                })}
                                            </td>
                                            <td>
                                                <div className="form-check form-switch">
                                                    <input className="form-check-input" type="checkbox" checked={it.isActive} onChange={(e) => toggleActive(it.id, e.target.checked)} />
                                                </div>
                                            </td>
                                            <td className="text-end">
                                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteRequest(it.id)}>Delete</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <ConfirmationModal
                show={showConfirm}
                title="Generate API Secret"
                message="Are you sure you want to generate new API Secret? This will create a new secret. Make sure to store it securely as it will not be shown again."
                onConfirm={handleGenerate}
                onCancel={() => setShowConfirm(false)}
                confirmText="Generate"
                confirmButtonClass="btn-primary"
                iconClass="fe fe-plus-circle text-primary"
            />

            {itemToDelete && (
                <ConfirmationModal
                    show={showDeleteConfirm}
                    title="Delete API Secret"
                    message={`Are you sure you want to delete the API secret? This action cannot be undone.`}
                    onConfirm={onDelete}
                    onCancel={() => setShowDeleteConfirm(false)}
                    confirmText="Delete"
                    confirmButtonClass="btn-danger"
                    iconClass="fe fe-trash-2 text-danger"
                />
            )}

            {generatedData && (
                <ModalWrapper show={showResultModal} onClose={() => setShowResultModal(false)} title="API Secret Generated">
                    <div className="alert alert-primary mb-4 d-flex align-items-center">
                        <i className="fe fe-alert-triangle me-2 fs-3"></i>
                        <div>
                            <h5 className="mb-1">Important!</h5>
                            <p className="mb-0">Make sure to copy and store your new API secret securely. You won't be able to view it again!</p>
                        </div>
                    </div>
                    <div className="mb-4">
                        <label className="form-label fw-bold">Client Secret</label>
                        <div className="input-group">
                            <code className="form-control bg-light">{generatedData.clientSecret}</code>
                            <button
                                className="btn btn-outline-primary"
                                type="button"
                                onClick={() => navigator.clipboard.writeText(generatedData.clientSecret)}
                                title="Copy Secret"
                            >
                                <i className="fe fe-copy"></i>
                            </button>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-primary" onClick={() => setShowResultModal(false)}>
                            <i className="fe fe-check"></i> Done
                        </button>
                    </div>
                </ModalWrapper>
            )}
        </div>
    );
};

export default DahabCredentialsList;
